import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Handler, useDrag } from "@use-gesture/react";
import classNames from "classnames";
import EventEmitter from "eventemitter3";
import {
  action,
  comparer,
  computed,
  IComputedValue,
  IObservableValue,
  observable,
  runInAction,
  toJS,
  untracked,
} from "mobx";
import { observer } from "mobx-react-lite";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { DEFAULT_LIBRARY_PANE_NAME, DEFAULT_PANE_WIDTH } from "../Constants";
import { syncCanvasIdAtom, userAtom } from "../SharedIframeState";
import {
  ActionType,
  EvaluateExpressionType,
  Pane,
  PaneInput,
  PaneOutput,
  PaneType,
  PaneViewMode,
  Rect,
} from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "../ui/Menu";
import { Tooltip } from "../ui/Tooltip";
import {
  areArraysEqual,
  assert,
  generatePaneId,
  generatePaneInputId,
  isMacPlatform,
  modifierKey,
  secondaryModifierKey,
} from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canCreateNewPaneComputed,
  canvasAtom,
  canvasTransformComputed,
  canvasViewport,
  focusedPaneIdAtom,
  globalValuesMobx,
  isSafeModeAtom,
  isShowingNonFocusedPopoverBox,
  isViewingCustomLayoutComputed,
  keyboardTransformPaneIdBox,
  showPaneSettingsPaneIdAtom,
} from "./CanvasState";
import { EvaluatorRunEmitter } from "./CanvasStateEffects";
import { getPaneInputBox, hasLoadedGlobalsComputed } from "./Evaluator";
import { PaneExpressionEditor } from "./ExpressionEditor";
import { FocusPaneEmitter } from "./FocusPane";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import {
  KeyboardCommandPriority,
  registerKeyboardCommand,
  registerKeyboardCommands,
} from "./Keyboard";
import { LogInspectorValues } from "./LogInspectorValues";
import { dragValueAtom, handleNewPaneCreate } from "./NewPane";
import { PaneBody } from "./PaneBody";
import { PaneEvaluateComponentSettingsButton } from "./PaneEvaluateComponentSettings";
import { PaneGlobalNameInput, PaneTransformButton } from "./PaneHeader";
import { PaneHeaderBar } from "./PaneHeaderBar";
import {
  PaneInputNodes,
  PaneRenderOutputConfigInputs,
  PopupPaneInputId,
} from "./PaneInput";
import { paneMutator } from "./PaneMutator";
import {
  PaneRenderOutputComponent,
  ReactErrorBoundary,
} from "./PaneRenderOutput";
import { PaneSplit } from "./PaneSplit";
import { PANE_ERROR_STACK_PREFIX } from "./SandboxConstants";
import {
  convertViewportPositionToCanvasOffset,
  reactionOnCondition,
  roundPosition,
  roundPositionToGrid,
  WAITING_CLASSNAME,
} from "./SandboxUtils";

const dragShield = document.createElement("div");
dragShield.className =
  "fixed top-0 left-0 right-0 bottom-0 z-99999 cursor-grabbing";

const ConsoleLogAllValuesButton = observer(
  ({
    consoleLogValuesMobx,
  }: {
    consoleLogValuesMobx: { runnerId: number; values: any[] };
  }) => {
    const bindDrag = useDrag(
      action<Handler<"drag">>(({ first, xy, last, cancel, event }) => {
        if (last) {
          if (dragValueAtom.value !== undefined) {
            handleNewPaneCreate();
            dragValueAtom.value = undefined;
          }
        } else {
          if (first) {
            if (!(event.target instanceof HTMLElement)) {
              cancel();
              return;
            }
            event.preventDefault();
          }
          dragValueAtom.value = {
            type: "value",
            name: undefined,
            value: toJS(consoleLogValuesMobx.values),
            position: xy,
          };
        }
      }),
      {
        transform: ([x, y]) =>
          untracked(() =>
            roundPositionToGrid(
              convertViewportPositionToCanvasOffset(
                [x, y],
                canvasTransformComputed.get(),
                canvasViewport.zoom
              ),
              1
            )
          ),
        pointer: { touch: true },
      }
    );

    return (
      <button
        {...bindDrag()}
        className="text-primary-500 hover:text-primary-600 transition"
      >
        {consoleLogValuesMobx.values.length} value
        {consoleLogValuesMobx.values.length !== 1 ? "s" : null}
      </button>
    );
  }
);

const ConsoleLogValuesPanel = observer(
  ({
    paneId,
    consoleLogValuesMobx,
    shouldReverseValues,
    setShouldReverseValues,
    onClear,
    panePortalElementBox,
  }: {
    paneId: string;
    consoleLogValuesMobx: { runnerId: number; values: any[] };
    shouldReverseValues: boolean;
    setShouldReverseValues: React.Dispatch<React.SetStateAction<boolean>>;
    onClear: () => void;
    panePortalElementBox: IObservableValue<HTMLElement | null>;
  }) => {
    const panePortalElement = panePortalElementBox.get();
    if (panePortalElement === null) {
      return null;
    }
    return createPortal(
      <div className="log-values-popout absolute right-full rounded-l rounded-br top-[31px] bottom-0 w-[257px] overflow-hidden bg-neutral-0 border-r border-neutral-200 transition">
        <div className="h-full flex flex-col overflow-hidden relative">
          <div className="bg-neutral-100 p-2 shrink-0">
            <div className="flex items-center mb-0.5">
              <span className="text-neutral-500 font-medium mr-1">
                console.log{" "}
                <ConsoleLogAllValuesButton
                  consoleLogValuesMobx={consoleLogValuesMobx}
                />
              </span>
              <Tooltip content="click to reverse order">
                <button
                  onClick={() => {
                    setShouldReverseValues((v) => !v);
                  }}
                  className="flex text-neutral-400 hover:text-neutral-600 transition"
                >
                  <FeatherIcon
                    icon={shouldReverseValues ? "arrow-up" : "arrow-down"}
                    size="sm"
                  />
                </button>
              </Tooltip>
            </div>
            <div className="text-2xs text-neutral-400">
              {modifierKey === "Meta" ? "⌘" : "ctrl"} click/drag value for
              reference
            </div>
          </div>
          <div
            className="grow overflow-auto"
            key={consoleLogValuesMobx.runnerId}
          >
            <LogInspectorValues
              paneId={paneId}
              values={
                shouldReverseValues
                  ? [...consoleLogValuesMobx.values].reverse()
                  : consoleLogValuesMobx.values
              }
              areValuesReversed={shouldReverseValues}
            />
          </div>
          <button
            onClick={() => onClear()}
            className="absolute top-1 right-1 flex p-1 text-neutral-400 hover:text-neutral-500 transition"
          >
            <FeatherIcon icon="x" size="md" />
          </button>
        </div>
      </div>,
      panePortalElement
    );
  }
);

function ExtractPaneNameOutput({
  paneId,
  onFixPane,
  isEditorFocused,
}: {
  paneId: string;
  onFixPane: () => void;
  isEditorFocused: boolean;
}) {
  return (
    <div className="p-2">
      <div className="text-neutral-500 mb-3">
        Instead of defining a variable, enter the (optional) name in the header.
        This pane is in{" "}
        <button
          onClick={action(() => {
            if (showPaneSettingsPaneIdAtom.value === paneId) {
              showPaneSettingsPaneIdAtom.value = undefined;
            }
          })}
          className="text-primary-600 font-medium hover:underline"
        >
          expression mode
        </button>{" "}
        so the code should contain only the value, like a spreadsheet!
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            onFixPane();
          }}
          className="button"
        >
          fix pane
        </button>
        {isEditorFocused ? (
          <span className="text-neutral-400">
            or press ⌃{isMacPlatform ? "⌘" : "⎇"}N
          </span>
        ) : null}
      </div>
    </div>
  );
}

const ErrorOutput = observer(
  ({
    paneId,
    paneComputed,
    error,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      | Extract<Pane, { type: PaneType.Evaluate | PaneType.EvaluateGlobal }>
      | undefined
    >;
    error: Error | string | undefined;
  }) => {
    let undefinedIdentifier: string | undefined;
    let topStackPaneId: string | undefined;
    let showSwitchToReactRenderOutput = false;
    if (error instanceof Error) {
      if (error.name === "ReferenceError") {
        const matches = /^(.*) is not defined$/.exec(error.message);
        if (matches !== null) {
          undefinedIdentifier = matches[1];
        }
      }
      if (error.stack !== undefined) {
        const matches = error.stack.match(
          new RegExp(`${PANE_ERROR_STACK_PREFIX}[a-zA-Z0-9]+`, "g")
        );
        if (matches !== null) {
          const paneIdsInStack = matches.map((match) =>
            match.replace(PANE_ERROR_STACK_PREFIX, "")
          );
          topStackPaneId = paneIdsInStack[0];
        }
      }
    } else if (error === "Unexpected token '<'") {
      const canSwitchToReactRenderOutput = computed(() => {
        const pane = paneComputed.get();
        return (
          pane?.type === PaneType.Evaluate && pane.renderOutput?.[0] !== "react"
        );
      }).get();
      showSwitchToReactRenderOutput = canSwitchToReactRenderOutput;
    }
    const matchingNamedPaneIds =
      undefinedIdentifier !== undefined
        ? computed(
            () =>
              canvasAtom.value.panes
                .filter((p) => p.name === undefinedIdentifier)
                .map((p) => p.id),
            { equals: comparer.structural }
          ).get()
        : [];
    const [undefinedIdentifierForFix, setUndefinedIdentifierForFix] = useState<
      string | undefined
    >(undefined);
    useEffect(() => {
      if (undefinedIdentifier !== undefined) {
        const timeout = setTimeout(() => {
          setUndefinedIdentifierForFix(undefinedIdentifier);
        }, 500);
        return () => {
          clearTimeout(timeout);
        };
      } else {
        setUndefinedIdentifierForFix(undefined);
      }
    }, [undefinedIdentifier]);

    return (
      <div className="p-2">
        <div className="font-mono-natto text-red-500">
          {error !== undefined ? error.toString() : "error"}
        </div>
        {topStackPaneId !== undefined && topStackPaneId !== paneId ? (
          <div className="mt-2">
            <div className="font-mono-natto text-neutral-500 mb-2">
              our erroring code is in another pane!
            </div>
            <div>
              <button
                onClick={() => {
                  FocusPaneEmitter.emit("focus", { paneId: topStackPaneId });
                }}
                className="button"
              >
                focus{" "}
                {canvasAtom.value.panes.find((p) => p.id === topStackPaneId)
                  ?.name ?? "erroring pane"}
              </button>
            </div>
          </div>
        ) : undefinedIdentifier !== undefined ? (
          <div
            className={classNames(
              "mt-4",
              undefinedIdentifier === undefinedIdentifierForFix
                ? "opacity-100 transition"
                : "opacity-0"
            )}
          >
            {matchingNamedPaneIds.length > 0 ? (
              <div className="mb-1">
                <button
                  onClick={() => {
                    canvasDispatch([
                      ActionType.PushPaneInput,
                      {
                        paneId,
                        paneInput: {
                          id: generatePaneInputId(),
                          name: undefinedIdentifier,
                          source: [matchingNamedPaneIds[0], 0],
                        },
                      },
                    ]);
                  }}
                  className="button"
                >
                  connect pane {undefinedIdentifier}
                </button>
              </div>
            ) : null}
            <div>
              <button
                onClick={() => {
                  const newPaneId = generatePaneId();
                  const pane = canvasAtom.value.panes.find(
                    (p) => p.id === paneId
                  )!;
                  canvasDispatch([
                    ActionType.CreatePane,
                    {
                      paneId: newPaneId,
                      type: PaneType.Evaluate,
                      position: [pane.rect[0] - 368, pane.rect[1]],
                      name: undefinedIdentifier,
                    },
                  ]);
                  canvasDispatch([
                    ActionType.PushPaneInput,
                    {
                      paneId,
                      paneInput: {
                        id: generatePaneInputId(),
                        name: undefinedIdentifier,
                        source: [newPaneId, 0],
                      },
                    },
                  ]);
                }}
                className={
                  matchingNamedPaneIds.length > 0
                    ? "text-2xs text-primary-400 hover:text-primary-500 transition"
                    : "button"
                }
              >
                {matchingNamedPaneIds.length > 0 ? "or " : ""}create input pane{" "}
                {undefinedIdentifier}
              </button>
            </div>
          </div>
        ) : showSwitchToReactRenderOutput ? (
          <div className="mt-4">
            <div className="text-neutral-400 mb-2">
              Are you trying to render React?
            </div>
            <button
              onClick={() => {
                canvasDispatch([
                  ActionType.SetPaneRenderOutput,
                  { paneId, renderOutput: ["react"] },
                ]);
                canvasDispatch([
                  ActionType.AddPaneBabelPlugin,
                  { paneId, babelPlugin: "transform-react-jsx" },
                ]);
              }}
              className="button"
            >
              switch to react render output
            </button>
            <div className="mt-1">
              <button
                onClick={() => {
                  canvasDispatch([
                    ActionType.AddPaneBabelPlugin,
                    { paneId, babelPlugin: "transform-react-jsx" },
                  ]);
                }}
                className="text-2xs text-primary-400 hover:text-primary-500 transition"
              >
                or just add react jsx transform
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
);

export const PaneEvaluateOutputRunEmitter = new EventEmitter();
const PaneEvaluateOutput = observer(
  ({
    paneId,
    paneType,
    paneComputed,
    outputBox,
    onFixExtractPaneName,
    isEditorFocused,
    panePortalElementBox,
  }: {
    paneId: string;
    paneType: PaneType;
    paneComputed: IComputedValue<
      | Extract<Pane, { type: PaneType.Evaluate | PaneType.EvaluateGlobal }>
      | undefined
    >;
    outputBox: IObservableValue<PaneOutput>;
    onFixExtractPaneName?: (() => void) | undefined;
    isEditorFocused: boolean;
    panePortalElementBox: IObservableValue<HTMLElement | null>;
  }) => {
    assert(
      paneType === PaneType.Evaluate ||
        paneType === PaneType.EvaluateGlobal ||
        paneType === PaneType.EvaluateTemplate
    );
    const activePaneState = activePaneStates.get(paneId)!;
    const isGlobalPane = paneType === PaneType.EvaluateGlobal;
    const expressionComputed = computed(
      () => paneComputed.get()?.expression ?? ""
    );

    const renderOutput = computed(() => {
      const pane = paneComputed.get();
      return pane?.type === PaneType.Evaluate ? pane.renderOutput : undefined;
    }).get();
    const outputGlobalName = computed(() => {
      const pane = paneComputed.get();
      return pane?.type === PaneType.EvaluateGlobal ? pane.name : undefined;
    }).get();

    const shouldAutorunPropComputed = useMemo(
      () =>
        computed(
          () => paneComputed.get()?.autorun === true && !isSafeModeAtom.value
        ),
      []
    );
    const shouldAutorunProp = shouldAutorunPropComputed.get();
    const shouldAutorunComputed = useMemo(
      () =>
        computed(
          () =>
            shouldAutorunPropComputed.get() &&
            (isGlobalPane || hasLoadedGlobalsComputed.get())
        ),
      []
    );
    const paneInputsComputed = computed<PaneInput[]>(
      () => {
        const pane = paneComputed.get();
        return pane !== undefined && "inputs" in pane
          ? pane.inputs
          : EMPTY_ARRAY;
      },
      { equals: comparer.structural }
    );
    const output = outputBox.get();
    const evaluatorFunctionLoadState = computed(() => {
      if (activePaneState.type !== PaneType.Evaluate) {
        return ["value"];
      }
      const f = activePaneState.functionGet.get();
      if (f[0] === "error") {
        return f;
      }
      return f[0];
    }).get();

    useEffect(
      action(() => {
        if (outputGlobalName !== undefined) {
          if (output[0] === "value") {
            globalValuesMobx[outputGlobalName] = output[1];
          } else {
            delete globalValuesMobx[outputGlobalName];
          }
        }
      }),
      [outputGlobalName, output]
    );
    const isManualRunWaitingForInput = computed(() => {
      if (shouldAutorunComputed.get()) {
        return false;
      }
      return paneInputsComputed
        .get()
        .some((paneInput) => getPaneInputBox(paneInput)?.get()[0] !== "value");
    }).get();

    const consoleLogValues =
      activePaneState.type === PaneType.Evaluate
        ? activePaneState.consoleLogValuesBox.get()
        : undefined;
    const [shouldReverseConsoleLogValues, setShouldReverseConsoleLogValues] =
      useState(false);
    const getRenderInput = useCallback(
      (renderInputSource: [paneId: string, outputIndex: number]) => {
        const [inputPaneId, outputIndex] = renderInputSource;
        const activePaneState = activePaneStates.get(inputPaneId);
        if (activePaneState === undefined) {
          return undefined;
        }
        return activePaneState.outputBoxes[outputIndex].get();
      },
      [activePaneStates]
    );

    const outputElementRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
      outputElementRef.current!.scrollTop = 0;
    }, [output]);

    return (
      <div
        className={classNames(
          "h-full relative overflow-hidden group select-text",
          {
            [WAITING_CLASSNAME]:
              evaluatorFunctionLoadState !== "value" && output[0] === "waiting",
          }
        )}
      >
        <div
          className={classNames(
            "h-full overflow-auto",
            !shouldAutorunProp &&
              (output[0] === "value" || output[0] === "error")
              ? classNames("transition-opacity", {
                  "opacity-50 hover:opacity-75": computed(
                    () =>
                      activePaneState.type === PaneType.Evaluate &&
                      !(
                        activePaneState.outputDepsBox.get()[0] ===
                          expressionComputed.get() &&
                        areArraysEqual(
                          activePaneState.outputDepsBox.get()[1],
                          paneInputsComputed
                            .get()
                            .map((paneInput) =>
                              getPaneInputBox(paneInput)?.get()
                            )
                        ) &&
                        activePaneState.outputDepsBox.get()[2] ===
                          ((
                            paneComputed.get() as
                              | Extract<Pane, { type: PaneType.Evaluate }>
                              | undefined
                          )?.expressionType ??
                            EvaluateExpressionType.Expression)
                      )
                  ).get(),
                })
              : undefined
          )}
          ref={outputElementRef}
        >
          {output[0] === "value" ? (
            <ReactErrorBoundary>
              <PaneRenderOutputComponent
                paneId={paneId}
                value={output[1]}
                renderOutput={renderOutput}
                disableDragExtract={paneType === PaneType.EvaluateGlobal}
                canChangeRenderOutput={
                  paneType === PaneType.Evaluate &&
                  activePaneState.viewMode.get() === PaneViewMode.Normal
                }
                getRenderInput={getRenderInput}
              />
            </ReactErrorBoundary>
          ) : onFixExtractPaneName !== undefined ? (
            <ExtractPaneNameOutput
              paneId={paneId}
              onFixPane={onFixExtractPaneName}
              isEditorFocused={isEditorFocused}
            />
          ) : output[0] === "error" ? (
            <ErrorOutput
              paneId={paneId}
              paneComputed={paneComputed}
              error={output[1]}
            />
          ) : output[0] === "running" ? null : evaluatorFunctionLoadState ===
            "loading" ? (
            <div className="font-mono-natto p-2 text-neutral-400">
              loading...
            </div>
          ) : evaluatorFunctionLoadState[0] === "error" ? (
            <div className="font-mono-natto p-2 text-red-400 break-words">
              {evaluatorFunctionLoadState[1]}
            </div>
          ) : !isGlobalPane && !hasLoadedGlobalsComputed.get() ? (
            <div className="font-mono-natto p-2 text-neutral-400">
              waiting for globals
            </div>
          ) : !shouldAutorunProp ? (
            isManualRunWaitingForInput ? (
              <div className="font-mono-natto italic p-2 text-neutral-400">
                waiting for input
              </div>
            ) : (
              <div className="p-2">
                <button
                  onClick={() => {
                    EvaluatorRunEmitter.emit(paneId);
                  }}
                  className="button"
                >
                  click to run
                </button>
              </div>
            )
          ) : output[0] === "waiting" && output[1] === "input" ? (
            <div className="font-mono-natto italic p-2 text-neutral-400">
              waiting for input
            </div>
          ) : null}
        </div>
        {consoleLogValues !== undefined ? (
          <ConsoleLogValuesPanel
            paneId={paneId}
            consoleLogValuesMobx={consoleLogValues}
            shouldReverseValues={shouldReverseConsoleLogValues}
            setShouldReverseValues={setShouldReverseConsoleLogValues}
            onClear={action(() => {
              assert(activePaneState.type === PaneType.Evaluate);
              activePaneState.consoleLogValuesBox.set(undefined);
            })}
            panePortalElementBox={panePortalElementBox}
          />
        ) : null}
      </div>
    );
  }
);

const DebugVisualizerMenuItem = observer(
  ({
    paneComputed,
    generatingDebugVisualizerStateBox,
    editorRef,
  }: {
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.Evaluate }> | undefined
    >;
    generatingDebugVisualizerStateBox: IObservableValue<
      "loading" | "error" | undefined
    >;
    editorRef: React.MutableRefObject<any | null>;
  }) => {
    let disableMessage: string | undefined;
    const isRunning = generatingDebugVisualizerStateBox.get() === "loading";
    if (syncCanvasIdAtom.value === undefined || userAtom.value === undefined) {
      disableMessage = "available only on\nsynced user canvases";
    } else {
      const paneHasInputs = computed(() => {
        const pane = paneComputed.get();
        return pane !== undefined && pane.inputs.length > 0;
      }).get();
      if (!paneHasInputs) {
        disableMessage = "pane has no inputs";
      } else {
        const hasInvalidPaneInput = computed(() => {
          const pane = paneComputed.get();
          if (pane === undefined) {
            return true;
          }
          for (const input of pane.inputs) {
            if (input.source === undefined || input.name === undefined) {
              return true;
            }
            const inputSourcePaneId = input.source[0];
            const inputSourcePaneOutput = activePaneStates
              .get(inputSourcePaneId)
              ?.outputBoxes[0].get();
            if (inputSourcePaneOutput?.[0] !== "value") {
              return true;
            }
          }
          return false;
        }).get();
        if (hasInvalidPaneInput) {
          disableMessage = "all pane inputs\nmust have values";
        }
      }
    }

    return (
      <Tooltip
        content={disableMessage ?? "generate code\nvisualizing inputs"}
        side="right"
      >
        <DropdownMenuItem
          onClick={async () => {
            if (disableMessage !== undefined || isRunning) {
              return;
            }
            const pane = paneComputed.get();
            if (pane === undefined) {
              return;
            }
            const inputs = pane.inputs.flatMap((input) => {
              if (input.source === undefined || input.name === undefined) {
                return [];
              }
              const inputSourcePaneId = input.source[0];
              const inputSourcePaneOutput = activePaneStates
                .get(inputSourcePaneId)
                ?.outputBoxes[0].get();
              if (inputSourcePaneOutput?.[0] !== "value") {
                return [];
              }
              let value = JSON.stringify(inputSourcePaneOutput[1]);
              if (value.length > 256) {
                value = value.substring(0, 256) + "...";
              }
              return [
                {
                  name: input.name,
                  paneId: inputSourcePaneId,
                  value,
                },
              ];
            });
            runInAction(() => {
              generatingDebugVisualizerStateBox.set("loading");
            });
            let generatedCode;
            try {
              const json = await ParentComlinkAPI.generateDebugVisualizerCode(
                pane.id,
                Object.fromEntries(
                  inputs.map((input) => [input.paneId, input.value])
                )
              );
              runInAction(() => {
                generatingDebugVisualizerStateBox.set(undefined);
              });
              generatedCode = json.code;
            } catch {
              runInAction(() => {
                generatingDebugVisualizerStateBox.set("error");
              });
            }
            if (generatedCode === undefined) {
              return;
            }
            generatedCode = generatedCode.replaceAll(/^  /gm, "");
            if (generatedCode.startsWith("\n")) {
              generatedCode = generatedCode.substring(1);
            }
            editorRef.current?.setCode(generatedCode);
            if (pane.expressionType !== EvaluateExpressionType.FunctionBody) {
              canvasDispatch([
                ActionType.SetPaneExpressionType,
                {
                  paneId: pane.id,
                  expressionType: EvaluateExpressionType.FunctionBody,
                },
              ]);
            }
            if (pane.renderOutput?.[0] !== "react") {
              canvasDispatch([
                ActionType.SetPaneRenderOutput,
                {
                  paneId: pane.id,
                  renderOutput: ["react"],
                },
              ]);
              canvasDispatch([
                ActionType.AddPaneBabelPlugin,
                {
                  paneId: pane.id,
                  babelPlugin: "transform-react-jsx",
                },
              ]);
            }
          }}
          disabled={disableMessage !== undefined}
        >
          <div>
            debug visualizer
            <span className="relative ml-1 top-[-5px] text-2xs font-mono-natto text-primary-400">
              alpha
            </span>
          </div>
        </DropdownMenuItem>
      </Tooltip>
    );
  }
);

function wrapEditorSelectionInCanvasPortal(
  pane: Extract<Pane, { type: PaneType.Evaluate }>,
  paneRect: Rect,
  editorRef: React.MutableRefObject<any | null>
) {
  let libraryPaneInput = pane.inputs.find((input) => {
    const inputPaneId = input.source?.[0];
    if (inputPaneId === undefined) {
      return false;
    }
    const sourcePane = canvasAtom.value.panes.find((p) => p.id === inputPaneId);
    return sourcePane?.type === PaneType.Library;
  });
  if (libraryPaneInput === undefined) {
    // connect the pane to a library pane if there isn't an existing input
    const libraryPane = canvasAtom.value.panes.find(
      (p) => p.type === PaneType.Library
    );
    let libraryPaneId: string;
    if (libraryPane !== undefined) {
      libraryPaneId = libraryPane.id;
    } else {
      libraryPaneId = generatePaneId();
      canvasDispatch([
        ActionType.CreatePane,
        {
          paneId: libraryPaneId,
          type: PaneType.Library,
          position: [paneRect[0] - DEFAULT_PANE_WIDTH - 96, paneRect[1]],
        },
      ]);
    }
    libraryPaneInput = {
      id: generatePaneInputId(),
      source: [libraryPaneId, 0],
      name: DEFAULT_LIBRARY_PANE_NAME,
    };
    canvasDispatch([
      ActionType.PushPaneInput,
      {
        paneId: pane.id,
        paneInput: libraryPaneInput,
      },
    ]);
  }
  editorRef.current?.wrapSelectionInCanvasPortal(
    paneRect,
    libraryPaneInput.name!
  );
}

const EditorContextMenuContent = observer(
  forwardRef(
    (
      {
        paneId,
        paneComputed,
        editorRef,
        expressionType,
      }: {
        paneId: string;
        paneComputed: IComputedValue<
          Extract<Pane, { type: PaneType.Evaluate }> | undefined
        >;
        editorRef: React.MutableRefObject<any | null>;
        expressionType: EvaluateExpressionType;
      },
      ref: React.ForwardedRef<HTMLDivElement>
    ) => {
      const activePaneState = activePaneStates.get(paneId);

      const shouldShowCanvasPortalOption = computed(() => {
        const pane = paneComputed.get();
        return (
          pane?.type === PaneType.Evaluate && pane.renderOutput?.[0] === "react"
        );
      }).get();

      return (
        <DropdownMenuContent sideOffset={4} align="start" ref={ref}>
          {expressionType !== EvaluateExpressionType.Text ? (
            <DropdownMenuItem
              onClick={() => {
                editorRef.current?.formatCode();
                editorRef.current?.focus();
              }}
              className="justify-between"
            >
              format code
              <span className="text-neutral-400">⌥⇧F</span>
            </DropdownMenuItem>
          ) : null}
          {activePaneState?.type === PaneType.Evaluate ? (
            <>
              <Tooltip
                content={"disable to show stack\ntraces in console"}
                side="right"
              >
                <DropdownMenuItem
                  onClick={action(() => {
                    activePaneState.isTryCatchDisabledBox.set(
                      !activePaneState.isTryCatchDisabledBox.get()
                    );
                  })}
                >
                  {activePaneState.isTryCatchDisabledBox.get()
                    ? "enable"
                    : "disable"}{" "}
                  try catch
                </DropdownMenuItem>
              </Tooltip>
              <Tooltip
                content={"inspect passing values\nin the code editor"}
                side="right"
              >
                <DropdownMenuItem
                  onClick={action(() => {
                    activePaneState.isLogInspectorEnabledBox.set(
                      !activePaneState.isLogInspectorEnabledBox.get()
                    );
                  })}
                >
                  <div>
                    {activePaneState.isLogInspectorEnabledBox.get()
                      ? "disable"
                      : "enable"}{" "}
                    inspector
                    <span className="relative ml-1 top-[-5px] text-2xs font-mono-natto text-primary-400">
                      beta
                    </span>
                  </div>
                </DropdownMenuItem>
              </Tooltip>
            </>
          ) : null}
          {/* <DebugVisualizerMenuItem
              paneComputed={paneComputed}
              generatingDebugVisualizerStateBox={
                generatingDebugVisualizerStateBox
              }
              editorRef={editorRef}
            /> */}
          {shouldShowCanvasPortalOption ? (
            <Tooltip content={"move React element\nout of pane"} side="right">
              <DropdownMenuItem
                onClick={() => {
                  runInAction(() => {
                    if (activePaneState === undefined) {
                      return;
                    }
                    const pane = paneComputed.get();
                    if (pane?.type !== PaneType.Evaluate) {
                      return;
                    }
                    wrapEditorSelectionInCanvasPortal(
                      pane,
                      activePaneState.rect.get(),
                      editorRef
                    );
                  });
                }}
              >
                {"insert <CanvasPortal>"}
              </DropdownMenuItem>
            </Tooltip>
          ) : null}
        </DropdownMenuContent>
      );
    }
  )
);

const EditorContextMenuButton = observer(
  ({
    paneId,
    paneComputed,
    isEditorFocused,
    editorRef,
    expressionType,
    generatingDebugVisualizerStateBox,
  }: {
    paneId: string;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.Evaluate }> | undefined
    >;
    isEditorFocused: boolean;
    editorRef: React.MutableRefObject<any | null>;
    expressionType: EvaluateExpressionType;
    generatingDebugVisualizerStateBox: IObservableValue<
      "loading" | "error" | undefined
    >;
  }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    return (
      <div
        className={classNames(
          "absolute left-full ml-2 top-8 focus-within:opacity-100 focus-within:pointer-events-auto transition-all",
          isEditorFocused || isDropdownOpen
            ? "opacity-100"
            : "opacity-0 pointer-events-none"
        )}
      >
        <DropdownMenuRoot
          open={isDropdownOpen}
          onOpenChange={(open) => {
            setIsDropdownOpen(open);
          }}
        >
          <DropdownMenuTrigger asChild={true}>
            <button
              className={classNames(
                "flex p-1 rounded bg-neutral-0 transition",
                !isDropdownOpen ? "opacity-50 hover:opacity-100" : undefined
              )}
            >
              <FeatherIcon icon="more-horizontal" size="sm" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuPortal>
            <EditorContextMenuContent
              paneId={paneId}
              paneComputed={paneComputed}
              editorRef={editorRef}
              expressionType={expressionType}
            />
          </DropdownMenuPortal>
        </DropdownMenuRoot>
      </div>
    );
  }
);

function AutorunDisablePopoverContent({
  paneId,
  onDismiss,
}: {
  paneId: string;
  onDismiss: () => void;
}) {
  return (
    <div className="bg-neutral-0 p-3 rounded max-w-[18rem] shadow-lg">
      <div className="text-neutral-500 mb-3">
        It looks like you might be writing a loop! Autorun was disabled to avoid
        infinite loops.
      </div>
      <div className="flex gap-2 items-center justify-end">
        <Tooltip
          content={
            <div className="keyboard-shortcut-tooltip">
              <span>⌃</span>
              <span>⌘</span>
              <span>a</span>
            </div>
          }
          delayDuration={500}
        >
          <button
            onClick={() => {
              canvasDispatch([
                ActionType.SetPaneAutorun,
                { paneId, autorun: true },
              ]);
              onDismiss();
            }}
            className="button"
          >
            re-enable autorun
          </button>
        </Tooltip>
        <Tooltip content="esc" delayDuration={500}>
          <button
            onClick={() => {
              onDismiss();
            }}
            className="text-neutral-400 hover:text-neutral-500 transition"
          >
            dismiss
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

const AutorunButton = observer(
  forwardRef(
    (
      {
        paneId,
        paneComputed,
      }: {
        paneId: string;
        paneComputed: IComputedValue<
          Extract<Pane, { type: PaneType.Evaluate }> | undefined
        >;
      },
      ref
    ) => {
      const [showAutorunDisablePopover, setShowAutorunDisableNotice] =
        useState(false);
      const autorun = computed(() => paneComputed.get()?.autorun).get();
      useImperativeHandle(
        ref,
        () => ({
          showAutorunDisablePopover: () => {
            setShowAutorunDisableNotice(true);
          },
        }),
        []
      );
      useEffect(() => {
        isShowingNonFocusedPopoverBox.set(showAutorunDisablePopover);
        return () => {
          isShowingNonFocusedPopoverBox.set(false);
        };
      }, [showAutorunDisablePopover]);
      useEffect(() => {
        if (autorun) {
          setShowAutorunDisableNotice(false);
        }
      }, [autorun]);

      if (autorun === undefined) {
        return null;
      }

      return (
        <PopoverPrimitive.Root
          open={showAutorunDisablePopover}
          onOpenChange={(open) => {
            if (!open) {
              setShowAutorunDisableNotice(false);
            }
          }}
        >
          <PopoverPrimitive.Anchor>
            <Tooltip
              content={
                <div>
                  <div>autorun is {autorun ? "enabled" : "disabled"}</div>
                  <div className="justify-center keyboard-shortcut-tooltip mt-1">
                    <span>⌃</span>
                    <span>⌘</span>
                    <span>a</span>
                  </div>
                </div>
              }
            >
              <button
                onClick={() => {
                  canvasDispatch([
                    ActionType.SetPaneAutorun,
                    { paneId, autorun: !autorun },
                  ]);
                }}
                className="h-[20px] flex px-1 items-center gap-1 bg-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-200 text-neutral-400 transition rounded-sm"
              >
                <span>auto</span>
                <FeatherIcon icon={autorun ? "check" : "slash"} size="xs" />
              </button>
            </Tooltip>
          </PopoverPrimitive.Anchor>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              side="top"
              sideOffset={12}
              onOpenAutoFocus={(e) => {
                e.preventDefault();
              }}
              className="popover-scale-in"
            >
              <AutorunDisablePopoverContent
                paneId={paneId}
                onDismiss={() => {
                  setShowAutorunDisableNotice(false);
                }}
              />
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      );
    }
  )
);

const EMPTY_ARRAY: any[] = [];
export const PaneEvaluateComponent = observer(
  ({
    paneId,
    paneType,
    paneComputed,
    panePortalElementBox,
    radixPortalContainerRef,
    isMaximized,
    isRenderingAsLayoutRunButton,
    toggleMaximize,
    focusOnMount,
    layoutId,
  }: {
    paneId: string;
    paneType: PaneType;
    paneComputed: IComputedValue<
      Extract<Pane, { type: PaneType.Evaluate }> | undefined
    >;
    panePortalElementBox: IObservableValue<HTMLElement | null>;
    radixPortalContainerRef: React.MutableRefObject<HTMLDivElement | null>;
    isMaximized: boolean;
    isRenderingAsLayoutRunButton: boolean;
    toggleMaximize: (action: any) => void;
    focusOnMount: boolean;
    layoutId: string | undefined;
  }) => {
    const activePaneState = activePaneStates.get(paneId)!;
    assert(
      activePaneState.type === PaneType.Evaluate ||
        activePaneState.type === PaneType.EvaluateGlobal
    );
    const expressionType = computed(() => {
      if (paneType !== PaneType.Evaluate) {
        return EvaluateExpressionType.Expression;
      }
      return (
        paneComputed.get()?.expressionType ?? EvaluateExpressionType.Expression
      );
    }).get();

    const isSafeMode = isSafeModeAtom.value;
    const isViewingCustomLayout = isViewingCustomLayoutComputed.get();

    const focusEditorOnMountRef = useRef(focusOnMount);
    const autorun = computed(() => paneComputed.get()?.autorun ?? false).get();
    const [isEditorFocused, setIsEditorFocused] = useState(false);
    useEffect(() => {
      if (focusEditorOnMountRef.current) {
        focusEditorOnMountRef.current = false;
      }
    }, [focusEditorOnMountRef.current]);
    const editorRef = useRef<any>(null);
    const [showPopupPaneInputId, setShowPopupPaneInputId] = useState<
      PopupPaneInputId | undefined
    >(undefined);

    const canExtractPaneName = computed(() => {
      if (
        paneType === PaneType.Evaluate &&
        expressionType === EvaluateExpressionType.Expression &&
        activePaneState.viewMode.get() !== PaneViewMode.Hidden
      ) {
        const matches = /^([a-zA-Z_$][0-9a-zA-Z_$]*)\s*=(?![=>])\s*/.exec(
          paneComputed.get()?.expression ?? ""
        );
        if (matches !== null) {
          return true;
        }
      }
      return false;
    }).get();

    const onFixExtractPaneName = useCallback(() => {
      const paneName = editorRef.current?.extractPaneName();
      if (paneName !== undefined) {
        canvasDispatch([ActionType.SetPaneName, { paneId, name: paneName }]);
      }
      editorRef.current?.focus();
    }, []);

    const onRenameInput = useCallback(
      async (paneInputId: string, newInputName: string) => {
        await editorRef.current?.renameInput(paneInputId, newInputName);
      },
      []
    );

    const viewMode = activePaneState.viewMode.get();

    useEffect(
      () =>
        reactionOnCondition(
          () => focusedPaneIdAtom.value === paneId,
          () =>
            registerKeyboardCommands([
              {
                name: "show pane settings",
                keys: [`Ctrl-${secondaryModifierKey}-,`],
                handler: action(() => {
                  showPaneSettingsPaneIdAtom.value = paneId;
                }),
              },
              {
                name: "toggle pane autorun",
                keys: [`Ctrl-${secondaryModifierKey}-a`],
                handler: () => {
                  canvasDispatch([
                    ActionType.SetPaneAutorun,
                    {
                      paneId: paneId,
                      autorun: !(paneComputed.get()?.autorun ?? false),
                    },
                  ]);
                },
              },
              {
                name: "toggle eval pane expression type",
                keys: [`Ctrl-${secondaryModifierKey}-e`],
                handler: () => {
                  const currentExpressionType =
                    paneComputed.get()?.expressionType ??
                    EvaluateExpressionType.Expression;
                  const newExpressionType = {
                    [EvaluateExpressionType.Expression]:
                      EvaluateExpressionType.FunctionBody,
                    [EvaluateExpressionType.FunctionBody]:
                      EvaluateExpressionType.Text,
                    [EvaluateExpressionType.Text]:
                      EvaluateExpressionType.Expression,
                  }[currentExpressionType];
                  canvasDispatch([
                    ActionType.SetPaneExpressionType,
                    {
                      paneId: paneId,
                      expressionType: newExpressionType,
                    },
                  ]);
                },
              },
              {
                name: "run pane",
                keys: ["Shift-Enter"],
                handler: () => {
                  EvaluatorRunEmitter.emit(`${paneId}`);
                },
              },
            ])
        ),
      []
    );
    useEffect(() => {
      if (canExtractPaneName) {
        return registerKeyboardCommand({
          name: "fix pane name",
          keys: [`Ctrl-${secondaryModifierKey}-n`],
          priority: KeyboardCommandPriority.High,
          handler: () => {
            const paneName = editorRef.current?.extractPaneName();
            if (paneName !== undefined) {
              canvasDispatch([
                ActionType.SetPaneName,
                { paneId, name: paneName },
              ]);
            }
          },
        });
      }
    }, [canExtractPaneName]);

    const [showAutorunDisableTooltip, setShowAutorunDisableTooltip] =
      useState(false);
    useEffect(() => {
      if (showAutorunDisableTooltip) {
        const timeout = setTimeout(() => {
          setShowAutorunDisableTooltip(false);
        }, 3000);
        return () => clearTimeout(timeout);
      }
    }, [showAutorunDisableTooltip]);

    const [generatingDebugVisualizerStateBox] = useState(() =>
      observable.box<"loading" | "error" | undefined>(undefined)
    );
    const autorunButtonRef = useRef<any | null>(null);

    return (
      <>
        <div
          className={classNames("flex flex-col h-full", {
            hidden: isRenderingAsLayoutRunButton,
          })}
        >
          <PopoverPrimitive.Root
            open={computed(
              () => showPaneSettingsPaneIdAtom.value === paneId
            ).get()}
            onOpenChange={action((open) => {
              if (open) {
                showPaneSettingsPaneIdAtom.value = paneId;
              } else if (showPaneSettingsPaneIdAtom.value === paneId) {
                showPaneSettingsPaneIdAtom.value = undefined;
              }
            })}
            modal={true}
          >
            <PopoverPrimitive.Anchor>
              <PaneHeaderBar
                paneId={paneId}
                paneComputed={paneComputed}
                shouldOutlineNameInput={canExtractPaneName}
                isMaximized={isMaximized}
              >
                {viewMode === PaneViewMode.Normal ? (
                  <>
                    {paneType === PaneType.Evaluate &&
                    !isViewingCustomLayout ? (
                      <PaneEvaluateComponentSettingsButton
                        paneId={paneId}
                        paneComputed={paneComputed}
                      />
                    ) : null}
                    <div className="flex items-center gap-1 ml-4 first:ml-0">
                      {!isViewingCustomLayout ? (
                        <AutorunButton
                          paneId={paneId}
                          paneComputed={paneComputed}
                          ref={autorunButtonRef}
                        />
                      ) : null}
                      <Tooltip
                        content={
                          <div className="flex gap-2 items-center">
                            <span>run pane</span>
                            <div className="keyboard-shortcut-tooltip">
                              <span>⇧</span>
                              <span>⏎</span>
                            </div>
                          </div>
                        }
                        delayDuration={500}
                      >
                        <button
                          onClick={() => EvaluatorRunEmitter.emit(paneId)}
                          className={classNames(
                            "w-5 h-5 flex items-center justify-center gap-1 rounded-sm transition",
                            autorun && !isSafeMode
                              ? "bg-neutral-100 hover:bg-accent-500 text-neutral-400 hover:text-neutral-0 dark:bg-neutral-200 dark:hover:bg-neutral-250 dark:hover:text-neutral-600"
                              : "bg-accent-400 text-neutral-0 hover:bg-accent-500 dark:bg-accent-200 dark:hover:bg-accent-300 dark:text-neutral-700"
                          )}
                        >
                          <FeatherIcon icon="play" size="xs" />
                        </button>
                      </Tooltip>
                    </div>
                  </>
                ) : null}
                <div className="grow" />
                {!isMaximized &&
                paneType === PaneType.Evaluate &&
                canCreateNewPaneComputed.get() ? (
                  <PaneTransformButton paneId={paneId} />
                ) : paneType === PaneType.EvaluateGlobal ? (
                  <>
                    <PaneGlobalNameInput
                      paneId={paneId}
                      paneComputed={paneComputed}
                      className="mr-1"
                    />
                    <FeatherIcon
                      icon="corner-right-up"
                      size="sm"
                      className="text-neutral-400"
                    />
                  </>
                ) : null}
              </PaneHeaderBar>
            </PopoverPrimitive.Anchor>
          </PopoverPrimitive.Root>
          <PaneBody viewMode={viewMode} paneComputed={paneComputed}>
            <PaneSplit
              paneId={paneId}
              paneType={paneType}
              activePaneState={activePaneState}
              isMaximized={isMaximized}
              inputElement={
                <PaneExpressionEditor
                  paneId={paneId}
                  paneComputed={paneComputed}
                  getInitialValue={() =>
                    untracked(() => paneComputed.get()!.expression)
                  }
                  focusOnMount={focusEditorOnMountRef.current}
                  toggleMaximize={toggleMaximize}
                  inspectorLogValuesBox={
                    activePaneState.type === PaneType.Evaluate
                      ? activePaneState.inspectorLogValuesBox
                      : undefined
                  }
                  onAddParensForLoopWithAutorun={() => {
                    autorunButtonRef.current?.showAutorunDisablePopover();
                    canvasDispatch([
                      ActionType.SetPaneAutorun,
                      { paneId, autorun: false },
                    ]);
                  }}
                  expressionType={expressionType}
                  setIsEditorFocused={setIsEditorFocused}
                  panePortalElementBox={panePortalElementBox}
                  ref={editorRef}
                />
              }
              outputElement={
                <PaneEvaluateOutput
                  paneId={paneId}
                  paneType={paneType}
                  paneComputed={paneComputed}
                  outputBox={activePaneState.outputBoxes[0]}
                  onFixExtractPaneName={
                    canExtractPaneName ? onFixExtractPaneName : undefined
                  }
                  isEditorFocused={isEditorFocused}
                  panePortalElementBox={panePortalElementBox}
                />
              }
            />
            {generatingDebugVisualizerStateBox.get() !== undefined ? (
              <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-neutral-500 text-2xs text-neutral-0 font-mono-natto rounded-sm flex items-center">
                {generatingDebugVisualizerStateBox.get() === "error" ? (
                  <>
                    <span>something went wrong..</span>
                    <button
                      onClick={action(() => {
                        generatingDebugVisualizerStateBox.set(undefined);
                      })}
                      className="flex ml-0.5"
                    >
                      <FeatherIcon icon="x" size="sm" />
                    </button>
                  </>
                ) : (
                  "generating.."
                )}
              </div>
            ) : null}
          </PaneBody>
          {viewMode !== PaneViewMode.Collapsed ? (
            <EditorContextMenuButton
              paneId={paneId}
              paneComputed={paneComputed}
              editorRef={editorRef}
              expressionType={expressionType}
              isEditorFocused={isEditorFocused}
              generatingDebugVisualizerStateBox={
                generatingDebugVisualizerStateBox
              }
            />
          ) : null}
          {viewMode === PaneViewMode.Normal ? (
            <div
              className={
                computed(
                  () => keyboardTransformPaneIdBox.get() === paneId
                ).get()
                  ? "opacity-50"
                  : undefined
              }
            >
              <PaneInputNodes
                paneId={paneId}
                paneType={paneType}
                paneComputed={paneComputed}
                showPopupPaneInputId={showPopupPaneInputId}
                setShowPopupPaneInputId={setShowPopupPaneInputId}
                layoutId={layoutId}
                onRenameInput={onRenameInput}
              />
              {paneType === PaneType.Evaluate ? (
                <PaneRenderOutputConfigInputs
                  paneComputed={paneComputed}
                  showPopupPaneInputId={showPopupPaneInputId}
                  setShowPopupPaneInputId={setShowPopupPaneInputId}
                  layoutId={layoutId}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        {isRenderingAsLayoutRunButton ? (
          <LayoutPaneRunButton paneId={paneId} />
        ) : null}
      </>
    );
  }
);

function LayoutPaneRunButton({ paneId }: { paneId: string }) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const rectComputed = activePaneStates.get(paneId)!.rect;
  const bindDrag = useDrag(
    action<Handler<"drag">>(
      ({
        offset,
        first,
        last,
        cancel,
        tap,
        canceled,
        memo = { startPosition: undefined },
      }) => {
        if (tap) {
          EvaluatorRunEmitter.emit(`${paneId}`);
          return;
        }
        if (first) {
          buttonRef.current!.style.cursor = "grabbing";
          const startPosition = [rectComputed.get()[0], rectComputed.get()[1]];
          return {
            startPosition,
          };
        }
        if (!canceled) {
          const newX = Math.floor(offset[0] / 16) * 16;
          const newY = Math.floor(offset[1] / 16) * 16;
          if (
            newX !== rectComputed.get()[0] ||
            newY !== rectComputed.get()[1]
          ) {
            paneMutator.updatePosition(paneId, [newX, newY]);
          }
        }
        if (last) {
          buttonRef.current!.style.cursor = "";
        }
        return memo;
      }
    ),
    {
      from: () => [rectComputed.get()[0], rectComputed.get()[1]],
      filterTaps: true,
      transform: ([x, y]) =>
        untracked(() =>
          roundPosition(
            convertViewportPositionToCanvasOffset(
              [x, y],
              canvasTransformComputed.get(),
              canvasViewport.zoom
            )
          )
        ),
    }
  );

  return (
    <button
      {...bindDrag()}
      className="w-full h-full flex items-center justify-center rounded transition bg-primary-400 text-neutral-0 hover:bg-primary-500 touch-none"
      ref={buttonRef}
    >
      <span className="mr-1">run</span>
      <FeatherIcon icon="play" size="sm" />
    </button>
  );
}
